import {
    ActionIcon,
    Alert,
    Anchor,
    Badge,
    Button,
    Group,
    Menu,
    Modal,
    Paper,
    Stack,
    Text,
    Title,
    Tooltip,
} from '@mantine/core'
import {IconAlertCircle, IconEdit, IconPlus, IconTrash} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'
import {useState} from 'react'

import {formatPrice} from '../lib/format-price'
import {RentalBillingModal} from '../rental-billing/rental-billing-modal'
import {useEditRights} from './edit-rights-context'
import {financialLineItemRateSummary} from './financial-line-item-form-utils'
import {type FinancialLineItemFormDraft, FinancialLineItemModal} from './financial-line-item-modal'
import type {JobDataLayer} from './job-data-layer'
import {getDefaultFeeRate} from './quote-builder-defaults'
import type {JobDetail, JobDetailFinancialLineItem} from './types'

type JobInvoicingSectionProps = {
    job: JobDetail
    jobDataLayer: JobDataLayer
    onJobUpdated: (job: JobDetail) => void
}

function feePresetDraft(feeKey: 'washOut' | 'disposal'): FinancialLineItemFormDraft {
    const rate = getDefaultFeeRate(feeKey)
    return {
        name: rate.defaultName,
        description: '',
        quantity: 1,
        unit: 'each',
        unitPrice: rate.price,
    }
}

export function JobInvoicingSection({job, jobDataLayer, onJobUpdated}: JobInvoicingSectionProps) {
    const {canEdit} = useEditRights()
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [lineItemModalOpened, setLineItemModalOpened] = useState(false)
    const [editingLineItem, setEditingLineItem] = useState<JobDetailFinancialLineItem | undefined>()
    const [presetDraft, setPresetDraft] = useState<FinancialLineItemFormDraft | undefined>()
    const [pendingArchiveLineItem, setPendingArchiveLineItem] = useState<
        JobDetailFinancialLineItem | undefined
    >()
    const [archiving, setArchiving] = useState(false)
    const [rentalBillingOpened, setRentalBillingOpened] = useState(false)
    const rentalBillingDisabled = job.status === 'canceled' || job.archivedAt != null

    function openAddModal(draft?: FinancialLineItemFormDraft) {
        setEditingLineItem(undefined)
        setPresetDraft(draft)
        setLineItemModalOpened(true)
    }

    function openEditModal(lineItem: JobDetailFinancialLineItem) {
        setEditingLineItem(lineItem)
        setPresetDraft(undefined)
        setLineItemModalOpened(true)
    }

    function closeLineItemModal() {
        setLineItemModalOpened(false)
        setEditingLineItem(undefined)
        setPresetDraft(undefined)
    }

    async function handleArchiveLineItem() {
        if (!pendingArchiveLineItem) {
            return
        }

        setArchiving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.archiveFinancialLineItem(
                job.id,
                pendingArchiveLineItem.id,
            )
            onJobUpdated(updatedJob)
            setPendingArchiveLineItem(undefined)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to remove charge')
        } finally {
            setArchiving(false)
        }
    }

    async function handleRentalChargesCreated() {
        const updatedJob = await jobDataLayer.getJob(job.id)
        onJobUpdated(updatedJob)
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={4}>
                        <Title order={3}>Charges</Title>
                        <Text size="sm" c="dimmed">
                            Charges billed in addition to the billing terms
                        </Text>
                    </Stack>
                    {canEdit ? (
                        <Group gap="xs" wrap="nowrap">
                            <Tooltip
                                label="Rental charges cannot be added to archived or canceled jobs."
                                disabled={!rentalBillingDisabled}
                            >
                                <span>
                                    <Button
                                        variant="light"
                                        size="sm"
                                        disabled={rentalBillingDisabled}
                                        onClick={() => setRentalBillingOpened(true)}
                                    >
                                        Add rental charges
                                    </Button>
                                </span>
                            </Tooltip>
                            <Menu position="bottom-end" withinPortal={false}>
                                <Menu.Target>
                                    <Button
                                        leftSection={<IconPlus size={16} />}
                                        variant="light"
                                        size="sm"
                                    >
                                        Add charge
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Item onClick={() => openAddModal()}>
                                        Custom charge
                                    </Menu.Item>
                                    <Menu.Item
                                        onClick={() => openAddModal(feePresetDraft('washOut'))}
                                    >
                                        Wash-out fee
                                    </Menu.Item>
                                    <Menu.Item
                                        onClick={() => openAddModal(feePresetDraft('disposal'))}
                                    >
                                        Disposal fee
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        </Group>
                    ) : null}
                </Group>

                <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Order-level invoicing"
                    color="blue"
                >
                    To select charges and create invoices, please go to the{' '}
                    <Anchor
                        component={Link}
                        to="/projects/$projectId"
                        params={{projectId: job.projectId}}
                        fw={500}
                        size="sm"
                    >
                        order page
                    </Anchor>
                    .
                </Alert>

                <Stack gap="sm">
                    {job.financialLineItems.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            No charges on this job yet.
                        </Text>
                    ) : (
                        <Stack gap="xs">
                            {job.financialLineItems.map((lineItem) => (
                                <Group key={lineItem.id} justify="space-between" wrap="nowrap">
                                    <Group
                                        justify="space-between"
                                        wrap="nowrap"
                                        style={{flex: 1}}
                                        align="flex-start"
                                    >
                                        <Stack gap={2}>
                                            <Group gap="xs">
                                                <Text size="sm" fw={500}>
                                                    {lineItem.name}
                                                </Text>
                                                {lineItem.invoiceId != null && (
                                                    <Badge size="xs" variant="light" color="gray">
                                                        Invoiced
                                                    </Badge>
                                                )}
                                            </Group>
                                            <Text size="xs" c="dimmed">
                                                {financialLineItemRateSummary(lineItem)}
                                            </Text>
                                            {lineItem.description && (
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                    style={{whiteSpace: 'pre-line'}}
                                                >
                                                    {lineItem.description}
                                                </Text>
                                            )}
                                        </Stack>
                                        <Text size="sm" fw={500} style={{marginRight: 16}}>
                                            {formatPrice(lineItem.totalPrice)}
                                        </Text>
                                    </Group>
                                    <Group
                                        gap={4}
                                        wrap="nowrap"
                                        style={{
                                            visibility:
                                                lineItem.invoiceId != null || !canEdit
                                                    ? 'hidden'
                                                    : undefined,
                                        }}
                                    >
                                        <ActionIcon
                                            variant="subtle"
                                            aria-label={`Edit ${lineItem.name}`}
                                            disabled={lineItem.isSynced}
                                            title={
                                                lineItem.isSynced
                                                    ? 'Synced charges cannot be edited'
                                                    : undefined
                                            }
                                            onClick={() => openEditModal(lineItem)}
                                        >
                                            <IconEdit size={16} />
                                        </ActionIcon>
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            aria-label={`Remove ${lineItem.name}`}
                                            onClick={() => setPendingArchiveLineItem(lineItem)}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Group>
                                </Group>
                            ))}
                        </Stack>
                    )}
                </Stack>

                {errorMessage && (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                )}
            </Stack>

            <FinancialLineItemModal
                opened={lineItemModalOpened}
                lineItem={editingLineItem}
                initialDraft={presetDraft}
                job={job}
                jobDataLayer={jobDataLayer}
                onClose={closeLineItemModal}
                onJobUpdated={onJobUpdated}
            />

            <RentalBillingModal
                jobId={job.id}
                projectId={job.projectId}
                opened={rentalBillingOpened}
                onClose={() => setRentalBillingOpened(false)}
                onChargesCreated={handleRentalChargesCreated}
            />

            <Modal
                opened={pendingArchiveLineItem != null}
                onClose={() => setPendingArchiveLineItem(undefined)}
                title="Remove charge"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Remove {pendingArchiveLineItem?.name} from this job? This charge will no
                        longer be available to invoice.
                    </Text>
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            onClick={() => setPendingArchiveLineItem(undefined)}
                            disabled={archiving}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            onClick={() => void handleArchiveLineItem()}
                            loading={archiving}
                        >
                            Remove charge
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Paper>
    )
}
